import React, { useState } from 'react'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import useWindowDimensions from '../_common/WindowDimesnions'
import { request } from '../../_network/request'
import { externalAppsURLs } from '../../_network/apiUrls'
import ExternalApplicationRegenerateSecret from './ExternalApplicationRegenerateSecret'
import CustomDialog from '../_common/CustomDialog'

const ExternalApplicationsItem = (props) => {
  const {
    id,
    name,
    app_client_id,
    setIsLoading,
    handleEditApplicationDialogOpen,
    handleDeleteApplicationDialogOpen,
  } = props
  const theme = useTheme()
  const { width } = useWindowDimensions()

  const clientSecretInitial = '************************************'
  const clientSecretLength = 32

  const [clientSecret, setClientSecret] = useState(null)
  const [regenerateSecretDialogOpen, setRegenerateSecretDialogOpen] = useState(false)
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false)
  const [notificationDialogProps, setNotificationDialogProps] = useState(null)

  const wrapperStyle = {
    minHeight: '110px',
    background: 'white',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.25)',
    borderRadius: '50px 20px 20px 20px',
    marginBottom: '20px',
  }

  const nameStyle = {
    width: '100%',
    height: '110px',
    color: 'white',
    background: theme.palette.violetsAreBlue,
    fontSize: width >= constants.lgBreakpoint ? '20px' : '16px',
    fontWeight: '500',
    borderRadius: '50px 0px', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const keyStyle = {
    color: theme.palette.grayX11,
    fontSize: '18px',
    fontWeight: '500',
  }

  const valueStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
  }

  const actionsButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '15px',
    textTransform: 'uppercase',
    height: '33px',
    borderRadius: '33px',
    padding: '0px 10px',

    '&:hover': {
      background: theme.palette.violetsAreBlue,
    },
  }

  const handleClickEdit = () => {
    handleEditApplicationDialogOpen(id)
  }

  const handleClickDelete = () => {
    handleDeleteApplicationDialogOpen(id)
  }

  const handleRegenerateSecretDialogOpen = (user_id) => {
    setRegenerateSecretDialogOpen(true)
  }

  const handleRegenerateSecretDialogClose = () => {
    setRegenerateSecretDialogOpen(false)
  }

  const handleNotificationDialogOpen = (dialogProps) => {
    setNotificationDialogOpen(true)
    setNotificationDialogProps(dialogProps)
  }

  const handleNotificationDialogClose = () => {
    setNotificationDialogOpen(false)
    setNotificationDialogProps(null)
  }

  const getShortenedClientSecret = () => {
    let result = ''

    if (clientSecret && clientSecret.length > clientSecretLength) {
      result = clientSecret.slice(0, clientSecretLength) + '...'
    } else {
      result = clientSecret
    }

    return result
  }

  const handleDecryptSecret = async () => {
    setIsLoading(true)

    const url = constants.core_service_domain + '/' + externalAppsURLs.getExternalApps + '/' + id + '/secret'

    const request_data = { url, method: 'GET' }   

    const response = await request(request_data)

    if (response && response.status_code && response.status_code < 400 && response.data && response.data.secret) {
      setClientSecret(response.data.secret)
    } else {
      setClientSecret(null)
    }

    setIsLoading(false)
  }

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(clientSecret)
      handleNotificationDialogOpen({
        title: defaultTexts.copy,
        text: defaultTexts.secretCopiedToClipboardText,
      })
    } catch (err) {
      
    }
  }
  
  return (
    <>      
      <Box sx={wrapperStyle}>
        <Grid container>
          <Grid size={{ xs: 12, md: 3, lg: 3 }}>
            <Box sx={nameStyle}>{name}</Box>
          </Grid>
          <Grid size={{ xs: 12, md: 8, lg: 8 }}>
            <Box sx={{ padding: '20px' }}>
              <Stack
                direction={ width >= constants.mdBreakpoint ? 'row' : 'column' }
                alignItems={ width >= constants.mdBreakpoint ? 'center' : 'flex-start' }
                justifyContent='flex-start'
                spacing={1}
              >
                <Box sx={keyStyle}>{defaultTexts.clientId}:</Box>
                <Box sx={valueStyle}>{app_client_id}</Box>
              </Stack>
              <Stack
                direction={ width >= constants.mdBreakpoint ? 'row' : 'column' }
                alignItems={ width >= constants.mdBreakpoint ? 'center' : 'flex-start' }
                justifyContent='flex-start'
                spacing={1}
              >
                <Box sx={keyStyle}>{defaultTexts.clientSecret}:</Box>
                <Box sx={{ ...valueStyle, width: '350px' }}>{clientSecret ? getShortenedClientSecret() : clientSecretInitial}</Box>
                {
                  clientSecret ?
                  <IconButton sx={{ color: theme.palette.grayX11 }} onClick={() => setClientSecret(null)}>
                    <VisibilityIcon />
                  </IconButton> :
                  <IconButton sx={{ color: theme.palette.grayX11 }} onClick={handleDecryptSecret}>
                    <VisibilityOffIcon />
                  </IconButton>
                }                
                
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  onClick={handleRegenerateSecretDialogOpen}
                  sx={actionsButtonStyle}
                >
                  {defaultTexts.regenerate}
                </Button>

                {
                  clientSecret &&
                  <Button
                    variant='contained'
                    disableRipple
                    disableElevation
                    onClick={handleCopySecret}
                    sx={actionsButtonStyle}
                  >
                    {defaultTexts.copy}
                  </Button>
                }
              </Stack>
            </Box>            
          </Grid>
          <Grid size={{ xs: 12, md: 1, lg: 1 }}>
            <Box>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent={ width >= constants.mdBreakpoint ? 'center' : 'flex-start' }
                spacing={{ xs: 0, md: 0, lg: 1 }}
                sx={{
                  width: '100%',
                  height: width >= constants.mdBreakpoint ? '70px' : 'auto',
                  margin: width >= constants.mdBreakpoint ? '20px 0px 0px 0px' : '0px 0px 10px 10px',
                  borderLeft: width >= constants.mdBreakpoint ? `${theme.palette.grayX11} 1px solid` : 'none',
                }}
              >
                <IconButton
                  sx={{ color: theme.palette.oriolesOrange }}
                  onClick={handleClickEdit}
                >
                  <EditIcon />
                </IconButton>              
                <IconButton
                  sx={{ color: theme.palette.coralRed }}
                  onClick={handleClickDelete}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <CustomDialog
        open={regenerateSecretDialogOpen}
        onClose={handleRegenerateSecretDialogClose}
        dialogTitle={{
          title: defaultTexts.regenerate,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          },
        }}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <ExternalApplicationRegenerateSecret
          id={id}
          name={name}
          setClientSecret={setClientSecret}
          handleRegenerateSecretDialogClose={handleRegenerateSecretDialogClose}
        />
      </CustomDialog>

      <CustomDialog
        open={notificationDialogOpen}
        onClose={handleNotificationDialogClose}
        dialogTitle={{
          title: (notificationDialogProps && notificationDialogProps.title) ? notificationDialogProps.title : '',
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          }
        }}
        dialogBody={{
          text: (notificationDialogProps && notificationDialogProps.text) ? notificationDialogProps.text : '',
          sx: {
            fontSize: '18px',
          }
        }}
      />
    </>
  )
}

export default ExternalApplicationsItem